import { useTranslation } from 'react-i18next';

import { IndicatorName, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { INDICATOR_DOMAINS } from '@/api/rest/resources/types/IndicatorProduct';
import { RecommendationProductCode } from '@/api/rest/resources/types/recommendations';
import { CSV_REPORT_TYPES } from '@/api/rest/resources/types/reports';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { DefaultWidgetNames, GraphTileType, WidgetType } from '@/api/rest/resources/types/widget';
import { GraphFact } from '@/components/Charts/types';
import { useGetActivePlotGroup } from '@/components/widgets/config/PlotGroupFilters';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useInSituIndicatorConfigurationOrgWidgetsByDomain } from '@/hooks/useInSituIndicatorConfigurationWidgets';
import { useProtectedAreasMethodology } from '@/hooks/useProtectedAreasMethodology';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { CsvExportButton } from '@/pages/shared/reports/components/CsvExportButton';
import { isNullish } from '@/utils/booleans';

import { useIsLimitedAccessUser } from '../hooks/useIsLimitedAccessUser';
import {
  useBiodiversityCells,
  useDefaultCustomCells,
  useHabitatIntactnessCells,
  useOrderedCustomCells,
} from '../plots/hooks/useGetCustomCells';
import { ProjectTypeAccessorKey } from '../projects/pages/project/pages/sustainableProduction/components/plot-list/constants';
import { LandPageLayout } from './components/LandPageLayout';

const PAGE_WIDGET_NAME = 'default_template_asset_pofh';
const PAGE_WIDGET_TYPE = WidgetType.t_page;

export const LandBiodiversityPage = () => {
  const { t } = useTranslation();
  const defaultWidgetConfiguration = useDefaultWidgetConfiguration();
  const customWidgetConfiguration = useActiveWidgetSlotConfiguration(
    PAGE_WIDGET_NAME,
    PAGE_WIDGET_TYPE,
    undefined,
    defaultWidgetConfiguration,
  );
  const widgetConfiguration = customWidgetConfiguration ?? defaultWidgetConfiguration;

  const defaultCells = [...useDefaultCustomCells(), ...useBiodiversityCells(), ...useHabitatIntactnessCells()];
  const orderedCustomCells = useOrderedCustomCells(defaultCells, [
    ProjectTypeAccessorKey.habitatIntactnessTrend_5y,
    ProjectTypeAccessorKey.habitatIntactness,
    ProjectTypeAccessorKey.biodiversityZoneTrend_1y,
    ProjectTypeAccessorKey.biodiversityZone,
  ]);

  return (
    <LandPageLayout
      signedInPageLayoutProps={{
        customizableWidgetTemplateProps: {
          widgetName: PAGE_WIDGET_NAME,
          widgetType: PAGE_WIDGET_TYPE,
          overrideLayoutConfiguration: defaultWidgetConfiguration,
        },
      }}
      navBarProps={{
        title: t('navbar.biodiversity'),
        children: <CsvExportButton reportType={CSV_REPORT_TYPES.BIODIVERSITY_CSV} className='w-fit font-normal' />,
      }}
      plotListProps={{
        customColumns: orderedCustomCells,
      }}
    >
      <WidgetSlotRenderer nestedWidget={widgetConfiguration} />
    </LandPageLayout>
  );
};

const useDefaultWidgetConfiguration = () => {
  const { t } = useTranslation();

  const organizationId = useOrganizationId();
  const isBiotara = ['70fd9c45-2ae1-4cb6-a99b-41767901cf17'].includes(organizationId);

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const activeFilters = useGetActivePlotGroup();

  const missingDataLabel = t('shared.ncaDetail.details.habitatIntactness.labels.missingDataContext');

  const { [IndicatorName.biodiversity_buffer_zone_pct_historic_graph]: biodiversityZonePercentHistoricGraph } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(IndicatorName.biodiversity_buffer_zone_pct_historic_graph);
  const bpHistoricData = biodiversityZonePercentHistoricGraph?.value;

  const hasGraphData = (bpHistoricData?.length ?? 0) > 0;

  const inSituWidgets = useInSituIndicatorConfigurationOrgWidgetsByDomain(INDICATOR_DOMAINS.BIODIVERSITY);

  const showSummaryCard =
    !isLimitedAccessUser && !isNullish(activeFilters) && activeFilters.length === 0 && hasGraphData;
  const { hasIbat } = useProtectedAreasMethodology();

  const pofhHeaderProps = {
    title: t('shared.ncaDetail.details.biodiversity.labels.protectedOnFarmHabitat'),
    headerTooltip: t('shared.ncaDetail.details.biodiversity.explainers.protectedOnFarmHabitat.body'),
    showToggle: false,
    showZoom: true,
    showGraphType: true,
  };

  const ndviHeaderProps = {
    title: t('shared.ncaDetail.details.ndvi.labels.vegetationCondition'),
    headerTooltip: t('shared.ncaDetail.details.ndvi.labels.vegetationConditionTooltip'),
    showToggle: false,
    showZoom: true,
    showGraphType: true,
  };

  const habitatIntactnessHeaderProps = {
    title: t('shared.ncaDetail.details.habitatIntactness.labels.habitatIntactness'),
    headerTooltip: `${t('shared.ncaDetail.details.habitatIntactness.labels.habitatIntactnessTooltip')}\n\n${missingDataLabel}`,
    showToggle: false,
    showZoom: false,
    showGraphType: true,
  };

  const habitatIntactnessThreatsBreakdownHeaderProps = {
    title: t('shared.ncaDetail.details.habitatIntactness.threatsBreakdown.labels.threatsBreakdown'),
    headerTooltip: `${t('shared.ncaDetail.details.habitatIntactness.threatsBreakdown.labels.threatsBreakdownTooltip')}\n\n${missingDataLabel}`,
    showToggle: false,
    showZoom: false,
    showGraphType: true,
  };

  const habitatIntactnessThreatsTableHeaderProps = {
    title: t('shared.ncaDetail.details.habitatIntactness.threatsTable.labels.threatsTable'),
    headerTooltip: missingDataLabel,
  };

  const canopyCoverHeaderProps = {
    title: t('shared.ncaDetail.details.forest.labels.canopyCover'),
    headerTooltip: t('shared.ncaDetail.details.forest.explainers.averageCanopyCover.body'),
    showToggle: false,
    showZoom: true,
    showGraphType: true,
  };

  const canopyHeightHeaderProps = {
    title: t('shared.ncaDetail.details.forest.labels.canopyHeight'),
    headerTooltip: t('shared.ncaDetail.details.forest.explainers.historicalCanopyHeight.body'),
    showToggle: false,
    showZoom: false,
    showGraphType: true,
  };

  const historicDeforestationHeaderProps = {
    title: t('shared.ncaDetail.details.deforestation.explainers.deforestation.title'),
    headerTooltip: t('shared.ncaDetail.details.deforestation.explainers.deforestation.body'),
    showToggle: false,
    showZoom: false,
    showGraphType: true,
  };

  const ecosystemClassificationHeaderProps = {
    title: t('shared.ncaDetail.ecosystemClassification.labels.ecosystemClassification'),
    headerTooltip: t('shared.ncaDetail.ecosystemClassification.labels.ecosystemClassificationTooltip'),
    showToggle: false,
    showZoom: false,
    showGraphType: false,
  };

  const habitatConnectivityHeaderProps = {
    title: t('shared.ncaDetail.details.habitatConnectivity.labels.title'),
    headerTooltip: t('shared.ncaDetail.details.habitatConnectivity.labels.structuralIntegrityTooltip'),
    showToggle: false,
    showZoom: false,
    showGraphType: false,
  };

  const protectedAreasHeaderProps = {
    title: t('shared.ncaDetail.details.protectedAreas.labels.protectedAreas'),
    headerTooltip: t(
      hasIbat
        ? 'shared.ncaDetail.details.protectedAreas.labels.graphHeadlineIbatTooltip'
        : 'shared.ncaDetail.details.protectedAreas.labels.graphHeadlineOsmTooltip',
    ),
    showToggle: false,
    showZoom: false,
    showGraphType: false,
  };

  const landcoverLanduseHeaderProps = {
    title: t('shared.ncaDetail.details.landCoverChange.labels.landCoverChange'),
    headerTooltip: t('shared.ncaDetail.details.landCoverChange.labels.graphHeadlineTooltip'),
    showToggle: false,
    showZoom: false,
    showGraphType: false,
  };

  return {
    name: PAGE_WIDGET_NAME,
    type: PAGE_WIDGET_TYPE,
    widgets: [
      {
        name: 'pofh_asset_summary_row',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'row',
          center: true,
          spacing: 6,
          className: 'flex-col lg:flex-row items-stretch',
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_fact_card_current_pofh,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.biodiversity_buffer_zone_pct,
              headerText: 'global.widget.currentPofh',
              tooltipText: 'global.widget.currentPofhTooltip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_historic_pofh_5y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.biodiversity_buffer_zone_pct_previous_5y_pct_change,
              headerText: 'global.widget.historic5yAvgPofhUplift',
              tooltipText: 'global.widget.historic5yUpliftTooltip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_projection_pofh_1y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.biodiversity_buffer_zone_pct_previous_1y_uplift_pct_change,
              headerText: 'global.widget.projection1yPofhUplift',
              tooltipText: 'global.widget.projection1yUpliftTooltip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_current_ndvi,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.vegetation_condition_ndvi,
              headerText: 'shared.ncaDetail.details.ndvi.labels.latestAnalysis',
              tooltipText: 'shared.ncaDetail.details.ndvi.labels.latestAnalysisTooltip',
            },
            visible: true,
          },
          {
            name: 'default_fact_card_current_structural_integrity',
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.structural_integrity,
              headerText: 'shared.ncaDetail.details.habitatConnectivity.labels.medianCoreHabitatConnectivity',
              tooltipText: 'shared.ncaDetail.details.habitatConnectivity.labels.medianCoreHabitatConnectivityTooltip',
              formatter: (fact: PlotReportFactElement<number>) => ({
                ...fact,
                // structural_integrity is an EII component which range [0,1], but this card shows percentages
                value: typeof fact?.value === 'number' ? fact.value * 100 : fact?.value,
                unit: UnitEnum['%'],
              }),
            },
            visible: true,
          },
        ],
      },
      {
        name: 'biodiversity_summary',
        type: WidgetType.o_recommendation_summary,
        visible: showSummaryCard,
        relatedData: {
          productCode: RecommendationProductCode.EK_PROTECTED_ON_FARM_HABITAT,
        },
      },
      {
        name: 'asset_pofh_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: pofhHeaderProps,
          graphTileType: GraphTileType.protected_on_farm_habitat,
        },
        visible: true,
      },
      {
        name: 'asset_ndvi_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: ndviHeaderProps,
          graphTileType: GraphTileType.ndvi,
        },
        visible: true,
      },
      {
        name: 'asset_historic_habitat_intactness_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: habitatIntactnessHeaderProps,
          graphTileType: GraphTileType.historic_habitat_intactness,
        },
        visible: true,
      },
      {
        name: 'asset_historic_habitat_intactness_threats_breakdown_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: habitatIntactnessThreatsBreakdownHeaderProps,
          graphTileType: GraphTileType.historic_habitat_intactness_threats_breakdown,
        },
        visible: true,
      },
      {
        name: 'asset_historic_habitat_intactness_threats_table',
        type: WidgetType.o_c_habitat_intactness_threats_table,
        relatedData: {
          headerProps: habitatIntactnessThreatsTableHeaderProps,
        },
        visible: true,
      },
      {
        name: 'asset_canopy_cover_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: canopyCoverHeaderProps,
          graphTileType: GraphTileType.canopy_cover,
        },
        visible: false,
      },
      {
        name: 'asset_canopy_height_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: canopyHeightHeaderProps,
          graphTileType: GraphTileType.canopy_height,
        },
        visible: false,
      },
      {
        name: 'asset_historic_deforestation_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: historicDeforestationHeaderProps,
          graphTileType: GraphTileType.historic_deforestation,
        },
        visible: true,
      },
      {
        name: DefaultWidgetNames.default_deforestation_degradation_drivers_decomposition,
        type: WidgetType.o_c_deforestation_degradation_drivers_decomposition,
        visible: isBiotara,
        relatedData: {
          styles: {
            body: 'pb-4',
          },
        },
      },
      {
        name: DefaultWidgetNames.default_forest_intelligence_chart,
        type: WidgetType.o_c_plot_group_forest_intelligence_chart,
        visible: isBiotara,
      },
      {
        name: 'asset_habitat_connectivity_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: habitatConnectivityHeaderProps,
          graphTileType: GraphTileType.habitat_connectivity,
        },
        visible: true,
      },
      {
        name: 'asset_landcover_landuse_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: landcoverLanduseHeaderProps,
          graphTileType: GraphTileType.landcover_landuse,
        },
        visible: false, // TODO: Enabel when MVP-10721 is implemented and all organizations have been recalculated
      },
      {
        name: 'asset_ecosystem_classification_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: ecosystemClassificationHeaderProps,
          graphTileType: GraphTileType.ecosystem_classification,
        },
        visible: true,
      },
      {
        name: 'asset_protected_areas_indicator_card',
        type: WidgetType.o_details_graph_card,
        relatedData: {
          headerProps: protectedAreasHeaderProps,
          graphTileType: GraphTileType.protected_areas,
        },
        visible: true,
      },
      ...inSituWidgets,
    ],
  };
};
